import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Eye, PenLine, User, X, Wrench } from 'lucide-react';
import { useActiveChild } from '../context/ActiveChildContext';
import { useAuth } from '../context/AuthContext';

const TONE = {
  work_as: { colour: '#f59e0b', Icon: PenLine, badge: 'you manage' },
  setup_for: { colour: '#34d399', Icon: Wrench, badge: 'you set up' },
  view: { colour: '#818cf8', Icon: Eye, badge: 'view only' },
};

/**
 * Global "Whose FuturePath?" switcher. Always visible when a parent is working on or viewing
 * someone else, so nobody can be confused about where their answers are going, and there is
 * always one click back to their own.
 */
export const SubjectBar = () => {
  const { user } = useAuth();
  const { subjects, activeSubject, isSelf, switchSubject } = useActiveChild();
  const [open, setOpen] = useState(false);

  // Shown whenever there's someone to switch to, and always when acting on someone else.
  if (isSelf && (!subjects || subjects.length <= 1)) return null;

  const tone = (!isSelf && TONE[activeSubject?.mode]) || { colour: '#22d3ee', Icon: User };
  const { Icon } = tone;
  const accent = tone.colour;
  const first = activeSubject?.name?.split(' ')[0];

  return (
    <div className="relative z-30 mb-4" data-testid="subject-bar">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border px-4 py-2.5"
        style={{ borderColor: `${accent}55`, background: `${accent}12` }}>
        <Icon className="w-4 h-4" style={{ color: accent }} />

        <p className="text-sm text-white font-medium" data-testid="subject-bar-label">
          {isSelf ? 'My FuturePath' : activeSubject?.banner}
        </p>
        {!isSelf && activeSubject?.mode === 'work_as' && (
          <span className="text-xs text-slate-300/90">
            Everything you do here saves to {first}, not to you.
          </span>
        )}
        {!isSelf && activeSubject?.mode === 'setup_for' && (
          <span className="text-xs text-slate-300/90">
            You're setting this up for {first} until they sign in and make it theirs.
          </span>
        )}
        {!isSelf && activeSubject?.mode === 'view' && (
          <span className="text-xs text-slate-300/90">
            {first} owns this now, read-only, their answers have to be theirs.
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {!isSelf && (
            <button onClick={() => switchSubject(null)} data-testid="subject-bar-exit"
              className="inline-flex items-center gap-1 rounded-full border border-white/20 px-3 py-1 text-xs text-slate-200 hover:border-white/50 hover:text-white transition-colors">
              <X className="w-3 h-3" /> back to my FuturePath
            </button>
          )}
          <button onClick={() => setOpen((o) => !o)} data-testid="subject-switcher-toggle"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-slate-900/60 px-3 py-1 text-xs text-slate-200 hover:border-white/40 transition-colors">
            Whose FuturePath? <ChevronDown className="w-3 h-3" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="absolute right-0 mt-2 w-80 rounded-xl border border-white/10 bg-slate-900/95 backdrop-blur-xl p-2 shadow-2xl"
            data-testid="subject-switcher-menu">
            {subjects.map((s) => {
              const active = s.is_self ? isSelf : s.id === activeSubject?.id;
              const t = TONE[s.mode] || {};
              return (
                <button key={s.id} onClick={() => switchSubject(s.is_self ? null : s.id)}
                  data-testid={`subject-option-${s.id}`}
                  className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors ${active ? 'bg-white/10' : 'hover:bg-white/5'}`}>
                  <p className="text-sm text-white font-medium flex items-center gap-2">
                    {s.is_self ? 'My FuturePath' : s.label}
                    {!s.is_self && <span className="text-xs text-slate-300">{s.age}</span>}
                    {!s.is_self && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ background: `${t.colour}22`, color: t.colour }}>
                        {t.badge}
                      </span>
                    )}
                  </p>
                  {!s.is_self && <p className="text-xs text-slate-300 mt-0.5">{s.access}</p>}
                  {!s.is_self && s.onboarding_complete === false && s.can_write && (
                    <p className="text-xs text-amber-300/90 mt-1">Not set up yet, start their FuturePath</p>
                  )}
                  {!s.is_self && s.onboarding_complete === false && !s.can_write && (
                    <p className="text-xs text-indigo-300/90 mt-1">
                      Waiting on {s.name?.split(' ')[0]} to sign in and set it up
                    </p>
                  )}
                </button>
              );
            })}
            <p className="text-[11px] text-slate-300 px-3 pt-2 pb-1">
              Signed in as {user?.full_name}. Switching reloads FuturePath for that person.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubjectBar;
